import { MapPin, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Fonts, Palette, Radius } from '@/constants/theme';
import {
  autocompletePlaces,
  getPlaceDetails,
  type PlaceDetail,
  type PlaceSuggestion,
} from '@/lib/places';
import { webNoAutofill, webNoOutline } from '@/lib/web-style';

/**
 * Single-line address input with Google Places suggestions appearing in a
 * floating list below. Picking a suggestion calls Place Details to grab a
 * structured payload (zip + city + state + lat/lon) and hands it to the
 * parent via onPick. Keeps its own visible string so the user can keep
 * typing or correct after a pick.
 */
export function AddressAutocomplete({
  value,
  onChangeText,
  onPick,
  onInputFocus,
  placeholder = 'Start typing your address…',
}: {
  value: string;
  onChangeText: (v: string) => void;
  onPick: (detail: PlaceDetail) => void;
  /** Fired when the input gains focus. Parents use it to scroll this
   *  field to the top of the viewport so the suggestion dropdown isn't
   *  buried under the software keyboard (Wave 59). */
  onInputFocus?: () => void;
  placeholder?: string;
}) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusedRef = useRef(false);
  // Value set BY a suggestion pick (Wave 73): the value-change effect
  // must not treat it as new typing — re-searching the picked address
  // reopened the dropdown with the same suggestion, which read as
  // "you have to click it twice" and left the list open during nav.
  const programmaticRef = useRef<string | null>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (programmaticRef.current === value) {
      // Our own fill — settle closed instead of re-searching it.
      setSuggestions([]);
      setOpen(false);
      return;
    }
    programmaticRef.current = null;
    if (!focusedRef.current) return;
    const term = value.trim();
    if (term.length < 3) {
      setSuggestions([]);
      setError(null);
      setOpen(false);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      const res = await autocompletePlaces(term);
      setLoading(false);
      if (!res.ok) {
        // Never surface raw exception text (Wave 57: a VPN-blocked
        // request showed "TypeError: Network request failed" verbatim).
        const networky = /network|timeout|failed to fetch|abort/i.test(res.error.message);
        setError(
          res.error.kind === 'no-key'
            ? 'Add a Google Places API key in Settings → Admin → API keys to enable suggestions.'
            : networky
            ? "Can't reach address lookup right now — check your connection (VPNs can block it), or just enter your ZIP below."
            : 'Address suggestions are unavailable right now — enter your ZIP below instead.'
        );
        setSuggestions([]);
        setOpen(true);
        return;
      }
      setError(null);
      setSuggestions(res.data);
      setOpen(res.data.length > 0);
    }, 220);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [value]);

  const pickSuggestion = async (s: PlaceSuggestion) => {
    // Optimistic fill from the suggestion's own primary + secondary text so
    // the field never looks "stuck empty" even if Place Details fails or
    // takes a moment. We refine the value if the details call succeeds.
    const fallback = [s.primary, s.secondary].filter(Boolean).join(', ');
    programmaticRef.current = fallback;
    onChangeText(fallback);
    setOpen(false);
    setLoading(true);
    const res = await getPlaceDetails(s.place_id);
    setLoading(false);
    if (!res.ok) {
      // Surface the error AND keep the open dropdown so the user can see it.
      // The fallback fill above means the address line is still usable.
      setError(`Couldn't load details: ${res.error.message}`);
      setOpen(true);
      onPick({
        address: fallback,
        zip: null,
        city: null,
        state: null,
        lat: null,
        lon: null,
      });
      return;
    }
    programmaticRef.current = res.data.address;
    onChangeText(res.data.address);
    onPick(res.data);
  };

  return (
    <View>
      <View style={styles.box}>
        <MapPin size={16} color={Palette.inkSoft} strokeWidth={1.8} />
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onFocus={() => {
            focusedRef.current = true;
            if (suggestions.length > 0) setOpen(true);
            onInputFocus?.();
          }}
          onBlur={() => {
            focusedRef.current = false;
            // Delay closing so the suggestion press registers first.
            setTimeout(() => setOpen(false), 180);
          }}
          placeholder={placeholder}
          placeholderTextColor={Palette.inkFaint}
          autoCorrect={false}
          autoCapitalize="words"
          // Wave 47 REVERSAL of the old "invite browser autofill" hints:
          // Chrome's own address panel rendered ON TOP of our Places
          // dropdown and filled only the text — skipping the structured
          // zip/lat/lon a real suggestion pick provides. This field has
          // its own suggestion UI, so the browser's must stay out of the
          // way. iOS keeps the keyboard-bar hint (doesn't overlap our
          // dropdown and can save typing).
          textContentType="fullStreetAddress"
          {...webNoAutofill('address-search')}
          style={[styles.input, webNoOutline]}
        />
        {value ? (
          <Pressable onPress={() => { onChangeText(''); setSuggestions([]); setOpen(false); }} hitSlop={8}>
            <X size={14} color={Palette.inkFaint} strokeWidth={2} />
          </Pressable>
        ) : null}
      </View>

      {open ? (
        <View style={styles.dropdown}>
          {loading ? (
            <Text style={styles.loadingText}>Looking up addresses…</Text>
          ) : error ? (
            <Text style={styles.errorText}>{error}</Text>
          ) : suggestions.length === 0 ? (
            <Text style={styles.loadingText}>No matches yet.</Text>
          ) : (
            // Capped + internally scrollable so the list fits in the
            // slice of screen left above the software keyboard —
            // suggestions below the fold were invisible (Wave 60).
            // keyboardShouldPersistTaps so a tap picks the suggestion
            // instead of just dismissing the keyboard; nestedScrollEnabled
            // for Android scroll-in-scroll.
            <ScrollView
              style={styles.suggestionScroll}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              showsVerticalScrollIndicator={suggestions.length > 3}>
              {suggestions.map((s, i) => (
                <Pressable
                  key={s.place_id}
                  onPress={() => pickSuggestion(s)}
                  style={({ pressed }) => [
                    styles.suggestion,
                    i < suggestions.length - 1 && styles.suggestionBorder,
                    pressed && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <Text style={styles.primary} numberOfLines={1}>{s.primary}</Text>
                  {s.secondary ? (
                    <Text style={styles.secondary} numberOfLines={1}>{s.secondary}</Text>
                  ) : null}
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
  },
  input: {
    flex: 1, fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
    padding: 0,
  },
  dropdown: {
    marginTop: 6,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  // ~4.5 rows tall — enough to show the half-row cut that signals
  // "scroll for more" without needing the space under the keyboard.
  suggestionScroll: { maxHeight: 265 },
  suggestion: {
    paddingHorizontal: 12, paddingVertical: 11,
  },
  suggestionBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  primary: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  secondary: { fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft, marginTop: 1 },
  loadingText: {
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.inkSoft,
  },
  errorText: {
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.clay, lineHeight: 17,
  },
});
